import { useState, useEffect } from 'react';
import {
  Users,
  Search,
  Filter,
  MoreVertical,
  Edit,
  UserCheck,
  UserX,
  Trash2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Download,
  Key
} from 'lucide-react';
import { getRoleDisplay, getGenderedRoleLabel, getAllRoles } from '../utils/roles';
import { UserRole } from '../types';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: UserRole;
  hospital_affiliation?: string;
  specialty?: string;
  years_of_experience?: number;
  is_verified: boolean;
  is_active: boolean;
  created_at: string;
  last_login?: string;
  updated_at?: string;
}

interface UserFilters {
  search: string;
  role: string;
  status: string;
  page: number;
  limit: number;
}

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  type: 'danger' | 'warning' | 'info';
  confirmText?: string;
  cancelText?: string;
}

function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  type,
  confirmText = 'Επιβεβαίωση',
  cancelText = 'Ακύρωση'
}: ConfirmationModalProps) {
  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'danger':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          icon: <AlertTriangle className="w-6 h-6 text-red-400" />,
          button: 'bg-red-600 hover:bg-red-700 text-white'
        };
      case 'warning':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          icon: <AlertTriangle className="w-6 h-6 text-yellow-400" />,
          button: 'bg-yellow-600 hover:bg-yellow-700 text-white'
        };
      default:
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          icon: <CheckCircle className="w-6 h-6 text-blue-400" />,
          button: 'bg-blue-600 hover:bg-blue-700 text-white'
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className={`flex items-center p-6 ${styles.bg} ${styles.border} border-b rounded-t-lg`}>
          {styles.icon}
          <h3 className="ml-3 text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="p-6">
          <p className="text-gray-700">{message}</p>
        </div>
        <div className="flex justify-end space-x-3 p-6 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 rounded-md transition-colors ${styles.button}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

interface RoleChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newRole: UserRole, reason: string) => void;
  user: User | null;
}

function RoleChangeModal({ isOpen, onClose, onConfirm, user }: RoleChangeModalProps) {
  const [newRole, setNewRole] = useState<UserRole>('member');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (user) {
      setNewRole(user.role);
      setReason('');
    }
  }, [user]);

  if (!isOpen || !user) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newRole !== user?.role && reason.trim()) {
      onConfirm(newRole, reason);
    }
  };

  const roles = getAllRoles();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center p-6 border-b">
          <Edit className="w-6 h-6 text-blue-400" />
          <h3 className="ml-3 text-lg font-semibold text-gray-900">
            Αλλαγή Ρόλου Χρήστη
          </h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              Χρήστης: <strong>{user.first_name} {user.last_name}</strong>
            </p>
            <p className="text-sm text-gray-600">
              Τρέχων ρόλος: <span className="font-medium">{getGenderedRoleLabel(user.role, user.sex)}</span>
            </p>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Νέος Ρόλος
            </label>
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as UserRole)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {roles.map(role => (
                <option key={role.key} value={role.key}>
                  {role.icon} {role.label}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Λόγος Αλλαγής *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Εισάγετε τον λόγο για την αλλαγή ρόλου..."
              required
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Ακύρωση
            </button>
            <button
              type="submit"
              disabled={newRole === user.role || !reason.trim()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Αλλαγή Ρόλου
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [actionType, setActionType] = useState<string | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState<any>({});
  
  const [filters, setFilters] = useState<UserFilters>({
    search: '',
    role: '',
    status: '',
    page: 1,
    limit: 20
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
    hasNext: false,
    hasPrevious: false
  });

  useEffect(() => {
    fetchUsers();
  }, [filters]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      const queryParams = new URLSearchParams();
      if (filters.search) queryParams.set('search', filters.search);
      if (filters.role) queryParams.set('role', filters.role);
      if (filters.status) queryParams.set('status', filters.status);
      queryParams.set('page', filters.page.toString());
      queryParams.set('limit', filters.limit.toString());

      const response = await fetch(`/api/v1/admin/users?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Αποτυχία λήψης χρηστών');
      }

      const result = await response.json();
      setUsers(result.data.users);
      setPagination(result.data.pagination);
    } catch (error) {
      console.error('Fetch users error:', error);
      setError(error instanceof Error ? error.message : 'Άγνωστο σφάλμα');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (newRole: UserRole, reason: string) => {
    console.log('[DEBUG] handleRoleChange called with:', { newRole, reason, selectedUser: selectedUser?.id });
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('accessToken');
      
      console.log('[DEBUG] Making role change API request:', {
        url: `/api/v1/admin/users/${selectedUser.id}/role`,
        body: { newRole, reason }
      });
      
      const response = await fetch(`/api/v1/admin/users/${selectedUser.id}/role`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ newRole, reason })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.log('[DEBUG] Role change API error:', { 
          status: response.status, 
          statusText: response.statusText,
          errorData 
        });
        throw new Error(errorData.message || 'Αποτυχία αλλαγής ρόλου');
      }

      const result = await response.json();
      console.log('[DEBUG] Role change successful:', result);

      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === selectedUser.id 
          ? { ...user, role: newRole }
          : user
      ));

      setShowRoleModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Role change error:', error);
      setError(error instanceof Error ? error.message : 'Σφάλμα αλλαγής ρόλου');
    }
  };

  const handleStatusChange = async (isActive: boolean, reason: string = '') => {
    console.log('[DEBUG] handleStatusChange called with:', { isActive, reason, selectedUser: selectedUser?.id });
    if (!selectedUser) {
      console.log('[DEBUG] No selectedUser, returning');
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');

      const response = await fetch(`/api/v1/admin/users/${selectedUser.id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isActive, reason })
      });

      if (!response.ok) {
        throw new Error('Αποτυχία αλλαγής κατάστασης');
      }

      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === selectedUser.id 
          ? { ...user, is_active: isActive }
          : user
      ));

      setShowConfirmModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Status change error:', error);
      setError(error instanceof Error ? error.message : 'Σφάλμα αλλαγής κατάστασης');
    }
  };

  const handleStatusChangeForUser = async (user: User, isActive: boolean, reason: string = '') => {
    console.log('[DEBUG] handleStatusChangeForUser called with:', { isActive, reason, userId: user.id });

    try {
      const token = localStorage.getItem('accessToken');

      const response = await fetch(`/api/v1/admin/users/${user.id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isActive, reason })
      });

      if (!response.ok) {
        throw new Error('Αποτυχία αλλαγής κατάστασης');
      }

      // Update local state
      setUsers(prev => prev.map(u => 
        u.id === user.id 
          ? { ...u, is_active: isActive }
          : u
      ));

      setShowConfirmModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Status change error:', error);
      setError(error instanceof Error ? error.message : 'Σφάλμα αλλαγής κατάστασης');
    }
  };

  const handleDeleteUser = async (reason: string = '') => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('accessToken');

      const response = await fetch(`/api/v1/admin/users/${selectedUser.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });

      if (!response.ok) {
        throw new Error('Αποτυχία διαγραφής χρήστη');
      }

      // Remove from local state
      setUsers(prev => prev.filter(user => user.id !== selectedUser.id));

      setShowConfirmModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Delete user error:', error);
      setError(error instanceof Error ? error.message : 'Σφάλμα διαγραφής χρήστη');
    }
  };

  const handleDeleteUserById = async (user: User, reason: string = '') => {
    console.log('[DEBUG] handleDeleteUserById called with:', { userId: user.id, reason });

    try {
      const token = localStorage.getItem('accessToken');

      const response = await fetch(`/api/v1/admin/users/${user.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });

      if (!response.ok) {
        throw new Error('Αποτυχία διαγραφής χρήστη');
      }

      // Remove from local state
      setUsers(prev => prev.filter(u => u.id !== user.id));

      setShowConfirmModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Delete user error:', error);
      setError(error instanceof Error ? error.message : 'Σφάλμα διαγραφής χρήστη');
    }
  };

  const handlePasswordReset = async () => {
    console.log('[DEBUG] handlePasswordReset called with selectedUser:', selectedUser?.id);
    if (!selectedUser) {
      console.log('[DEBUG] No selectedUser for password reset, returning');
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');

      // Generate a simple new password
      const newPassword = Math.random().toString(36).slice(-8);

      const response = await fetch(`/api/v1/admin/users/${selectedUser.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          newPassword,
          reason: `Επαναφορά κωδικού από διαχειριστή στις ${new Date().toLocaleDateString('el-GR')}`
        })
      });

      if (!response.ok) {
        throw new Error('Αποτυχία επαναφοράς κωδικού');
      }

      // Show success message with new password
      alert(`Ο κωδικός επαναφέρθηκε επιτυχώς!\nΝέος κωδικός: ${newPassword}\nΠαρακαλώ αντιγράψτε τον κωδικό και στείλτε τον στον χρήστη με ασφαλή τρόπο.`);

      setShowConfirmModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Password reset error:', error);
      setError(error instanceof Error ? error.message : 'Σφάλμα επαναφοράς κωδικού');
    }
  };

  const handlePasswordResetForUser = async (user: User) => {
    console.log('[DEBUG] handlePasswordResetForUser called with user:', user.id);

    try {
      const token = localStorage.getItem('accessToken');

      // Generate a simple new password
      const newPassword = Math.random().toString(36).slice(-8);

      const response = await fetch(`/api/v1/admin/users/${user.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          newPassword,
          reason: `Επαναφορά κωδικού από διαχειριστή στις ${new Date().toLocaleDateString('el-GR')}`
        })
      });

      if (!response.ok) {
        throw new Error('Αποτυχία επαναφοράς κωδικού');
      }

      // Show success message with new password
      alert(`Ο κωδικός επαναφέρθηκε επιτυχώς!\nΝέος κωδικός: ${newPassword}\nΠαρακαλώ αντιγράψτε τον κωδικό και στείλτε τον στον χρήστη με ασφαλή τρόπο.`);

      setShowConfirmModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Password reset error:', error);
      setError(error instanceof Error ? error.message : 'Σφάλμα επαναφοράς κωδικού');
    }
  };

  const openConfirmModal = (user: User, action: string) => {
    setSelectedUser(user);
    setActionType(action);

    // Create the handler function that captures the current user and action
    const handleConfirmAction = () => {
      console.log('[DEBUG] handleConfirmAction called with action:', action, 'user:', user.id);
      switch (action) {
        case 'activate':
          console.log('[DEBUG] Calling handleStatusChange(true)');
          handleStatusChangeForUser(user, true);
          break;
        case 'deactivate':
          console.log('[DEBUG] Calling handleStatusChange(false)');
          handleStatusChangeForUser(user, false);
          break;
        case 'delete':
          console.log('[DEBUG] Calling handleDeleteUser()');
          handleDeleteUserById(user);
          break;
        case 'reset-password':
          console.log('[DEBUG] Calling handlePasswordReset()');
          handlePasswordResetForUser(user);
          break;
        default:
          console.log('[DEBUG] Unknown action:', action);
      }
    };

    let config = {};
    switch (action) {
      case 'activate':
        config = {
          title: 'Ενεργοποίηση Χρήστη',
          message: `Είστε σίγουροι ότι θέλετε να ενεργοποιήσετε τον χρήστη "${user.first_name} ${user.last_name}";`,
          type: 'info',
          confirmText: 'Ενεργοποίηση',
          onConfirm: handleConfirmAction
        };
        break;
      case 'deactivate':
        config = {
          title: 'Απενεργοποίηση Χρήστη',
          message: `Είστε σίγουροι ότι θέλετε να απενεργοποιήσετε τον χρήστη "${user.first_name} ${user.last_name}";`,
          type: 'warning',
          confirmText: 'Απενεργοποίηση',
          onConfirm: handleConfirmAction
        };
        break;
      case 'delete':
        config = {
          title: 'Διαγραφή Χρήστη',
          message: `Είστε σίγουροι ότι θέλετε να διαγράψετε τον χρήστη "${user.first_name} ${user.last_name}"; Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.`,
          type: 'danger',
          confirmText: 'Διαγραφή',
          onConfirm: handleConfirmAction
        };
        break;
      case 'reset-password':
        config = {
          title: 'Επαναφορά Κωδικού',
          message: `Θα δημιουργηθεί νέος κωδικός για τον χρήστη "${user.first_name} ${user.last_name}". Συνέχεια;`,
          type: 'warning',
          confirmText: 'Επαναφορά Κωδικού',
          onConfirm: handleConfirmAction
        };
        break;
    }

    console.log('[DEBUG] openConfirmModal - setting config:', {
      action,
      user: user.first_name + ' ' + user.last_name,
      config,
      onConfirm: typeof config.onConfirm
    });
    setConfirmConfig(config);
    setShowConfirmModal(true);
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const getStatusBadge = (user: User) => {
    if (!user.is_active) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="w-3 h-3 mr-1" />
          Ανενεργός
        </span>
      );
    }
    if (!user.is_verified) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Μη επιβεβαιωμένο email
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircle className="w-3 h-3 mr-1" />
        Ενεργός
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-medical-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <XCircle className="w-5 h-5 text-red-400 mr-2" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Σφάλμα φόρτωσης</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
        <button
          onClick={fetchUsers}
          className="mt-4 bg-red-100 hover:bg-red-200 text-red-800 px-4 py-2 rounded-md text-sm font-medium transition-colors"
        >
          Δοκιμή ξανά
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Διαχείριση Χρηστών</h1>
            <p className="text-gray-600 mt-1">
              Διαχείριση {pagination.total.toLocaleString()} χρηστών της πλατφόρμας
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={fetchUsers}
              className="flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Ανανέωση
            </button>
            <button className="flex items-center px-3 py-2 bg-medical-600 hover:bg-medical-700 text-white rounded-md transition-colors">
              <Download className="w-4 h-4 mr-2" />
              Εξαγωγή
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Αναζήτηση
            </label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
                placeholder="Όνομα, email..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ρόλος
            </label>
            <select
              value={filters.role}
              onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value, page: 1 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Όλοι οι ρόλοι</option>
              {getAllRoles().map(role => (
                <option key={role.key} value={role.key}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Κατάσταση
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Όλες οι καταστάσεις</option>
              <option value="active">Ενεργοί</option>
              <option value="inactive">Ανενεργοί</option>
              <option value="verified">Επιβεβαιωμένοι</option>
              <option value="unverified">Μη επιβεβαιωμένοι</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ανά σελίδα
            </label>
            <select
              value={filters.limit}
              onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Χρήστης
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ρόλος
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Κατάσταση
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Εγγραφή
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Τελευταία Σύνδεση
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ενέργειες
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {user.first_name} {user.last_name}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                      {user.hospital_affiliation && (
                        <div className="text-xs text-gray-400">{user.hospital_affiliation}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm">{getGenderedRoleLabel(user.role, user.sex)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(user)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(user.created_at).toLocaleDateString('el-GR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.last_login 
                      ? new Date(user.last_login).toLocaleDateString('el-GR')
                      : 'Ποτέ'
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setShowRoleModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded"
                        title="Αλλαγή ρόλου"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => openConfirmModal(user, 'reset-password')}
                        className="text-purple-600 hover:text-purple-900 p-1 rounded"
                        title="Επαναφορά Κωδικού"
                      >
                        <Key className="w-4 h-4" />
                      </button>
                      
                      {user.is_active ? (
                        <button
                          onClick={() => openConfirmModal(user, 'deactivate')}
                          className="text-yellow-600 hover:text-yellow-900 p-1 rounded"
                          title="Απενεργοποίηση"
                        >
                          <UserX className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => openConfirmModal(user, 'activate')}
                          className="text-green-600 hover:text-green-900 p-1 rounded"
                          title="Ενεργοποίηση"
                        >
                          <UserCheck className="w-4 h-4" />
                        </button>
                      )}
                      
                      <button
                        onClick={() => openConfirmModal(user, 'delete')}
                        className="text-red-600 hover:text-red-900 p-1 rounded"
                        title="Διαγραφή"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.hasPrevious}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Προηγούμενη
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.hasNext}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Επόμενη
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Εμφάνιση{' '}
                  <span className="font-medium">
                    {((pagination.page - 1) * pagination.limit) + 1}
                  </span>{' '}
                  έως{' '}
                  <span className="font-medium">
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span>{' '}
                  από{' '}
                  <span className="font-medium">{pagination.total}</span> αποτελέσματα
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.hasPrevious}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Προηγούμενη
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    const page = Math.max(1, Math.min(
                      pagination.page - 2 + i,
                      pagination.pages - 4 + i
                    ));
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === pagination.page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.hasNext}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Επόμενη
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <RoleChangeModal
        isOpen={showRoleModal}
        onClose={() => {
          setShowRoleModal(false);
          setSelectedUser(null);
        }}
        onConfirm={handleRoleChange}
        user={selectedUser}
      />

      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setSelectedUser(null);
          setConfirmConfig({});
        }}
        title={confirmConfig.title || ''}
        message={confirmConfig.message || ''}
        type={confirmConfig.type || 'info'}
        confirmText={confirmConfig.confirmText}
        cancelText={confirmConfig.cancelText}
        onConfirm={() => {
          console.log('[DEBUG] ConfirmationModal onConfirm triggered, calling:', typeof confirmConfig.onConfirm);
          if (confirmConfig.onConfirm) {
            confirmConfig.onConfirm();
          } else {
            console.log('[DEBUG] No onConfirm function found in config!');
          }
        }}
      />
    </div>
  );
}